import type { WorkspacePlan } from '@katsquare/types';

/** Human-readable plan names, shared wherever a plan is displayed. */
export const PLAN_LABEL: Record<WorkspacePlan, string> = {
  FREE: 'Free',
  PRO: 'Pro',
  BUSINESS: 'Business',
};

/** A pricing tier as presented on the marketing page and the in-app plan picker. */
export interface PricingTier {
  plan: WorkspacePlan;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  popular?: boolean;
}

/**
 * Single source of truth for plan marketing copy — shared by the public landing
 * page and the in-app pricing/settings picker so prices and features never drift.
 * The enforced quotas behind these bullets live in `PLAN_LIMITS` (@katsquare/types).
 */
export const PRICING_TIERS: PricingTier[] = [
  {
    plan: 'FREE',
    name: 'Free',
    price: '₹0',
    period: 'forever',
    tagline: 'For trying things out.',
    features: [
      '1 manager · 2 reviewers',
      '1 PDF proof',
      '10 MB total storage',
      'PDF proofing & comments',
      'Single approval gate',
    ],
  },
  {
    plan: 'PRO',
    name: 'Pro',
    price: '₹1,999',
    period: 'per editor / mo',
    tagline: 'For magazines shipping every month.',
    features: [
      '1 manager · 5 reviewers',
      'Up to 2 magazines',
      'Unlimited PDF proofs',
      '1 GB storage',
      'Version compare & search',
      'Reviewer & admin roles',
      'Recycle bin & activity log',
    ],
  },
  {
    plan: 'BUSINESS',
    name: 'Business',
    price: '₹3,999',
    period: 'per editor / mo',
    tagline: 'For publishing houses at scale.',
    features: [
      '5 managers · 50 reviewers',
      'Everything in Pro',
      'Unlimited magazines & workspaces',
      '5 GB storage',
      'Priority rendering',
      'SSO & audit exports',
      'Priority support',
    ],
  },
];
